import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, auth } from '../api/client';
import { OrgLayout } from '../components/OrgLayout';
import { StudentLayout } from '../components/StudentLayout';
import { RecordInfoButton } from '../components/RecordInfoButton';
import { useToast } from '../components/Toast';
import { answerSheetsZipName, downloadAnswerSheetsZip } from '../lib/answerSheet';
import type { ExamSummary, ReviewBundle } from '../types/models';

/** Format a Date as the value a <input type="datetime-local"> expects (local time, no TZ). */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Fetch review bundles for many attempts with a small concurrency cap. */
async function fetchBundles(ids: string[], onProgress: (done: number) => void): Promise<ReviewBundle[]> {
  const out: ReviewBundle[] = new Array(ids.length);
  let next = 0, done = 0;
  const workers = Array.from({ length: Math.min(5, ids.length) }, async () => {
    for (let i = next++; i < ids.length; i = next++) {
      out[i] = await api.getReviewBundle(ids[i]);
      onProgress(++done);
    }
  });
  await Promise.all(workers);
  return out;
}

type ExamTab = 'all' | 'draft' | 'published' | 'attempted' | 'toEvaluate' | 'results';

export function ExamListPage({ mode = 'all' }: { mode?: ExamTab } = {}) {
  const toast = useToast();
  const { orgId } = useParams();
  const embedded = !!orgId; // rendered inside the org sidebar (admin) vs standalone (faculty/student)
  const nav = useNavigate();
  const [exams, setExams] = useState<ExamSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [devEnabled, setDevEnabled] = useState(false);
  const user = auth.getUser();

  // Cascading dept → program → batch filters (staff list views).
  const [fDept, setFDept] = useState('');
  const [fProgram, setFProgram] = useState('');
  const [fBatch, setFBatch] = useState('');

  // "Publish result" modal: which exam, the chosen datetime-local value, and busy state.
  const [resultFor, setResultFor] = useState<ExamSummary | null>(null);
  const [publishAt, setPublishAt] = useState('');
  const [savingResult, setSavingResult] = useState(false);

  // Answer-sheet download (per exam): the exam currently downloading + its progress.
  const [dl, setDl] = useState<{ examId: string; done: number; total: number } | null>(null);

  function openResultModal(e: ExamSummary) {
    setPublishAt(toLocalInput(e.resultPublishAt ? new Date(e.resultPublishAt) : new Date()));
    setResultFor(e);
  }

  async function confirmPublishResult() {
    if (!resultFor || !publishAt) return;
    setSavingResult(true);
    try {
      await api.publishResults(resultFor.id, new Date(publishAt).toISOString());
      setResultFor(null);
      setExams(await api.listExams());
      toast.success('Result publish scheduled.');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to publish results.';
      alert(msg);
      toast.error(msg);
    } finally {
      setSavingResult(false);
    }
  }

  // Download every submission's answer sheet for one exam as a ZIP of PDFs.
  async function downloadExamSheets(e: ExamSummary) {
    if (dl) return;
    setDl({ examId: e.id, done: 0, total: e.submissionCount });
    try {
      const attempts = await api.listExamAttempts(e.id);
      if (attempts.length === 0) {
        toast.info('No submissions to download yet.');
        return;
      }
      setDl({ examId: e.id, done: 0, total: attempts.length });
      const bundles = await fetchBundles(
        attempts.map(a => a.attemptId),
        done => setDl({ examId: e.id, done, total: attempts.length }));
      await downloadAnswerSheetsZip(bundles, answerSheetsZipName(e.title));
      toast.success(`Downloaded ${bundles.length} answer sheet${bundles.length === 1 ? '' : 's'}.`);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to download answer sheets.');
    } finally {
      setDl(null);
    }
  }

  const newExamPath = embedded ? `/org/${orgId}/exams/new` : '/author/new';
  const authorPath = (id: string) => embedded ? `/org/${orgId}/exams/${id}` : `/author/${id}`;
  const reviewPath = (id: string) => embedded ? `/org/${orgId}/exams/${id}/review` : `/exam/${id}/review`;

  useEffect(() => {
    api.listExams().then(setExams).catch(e => setError(e.message));
    api.devEnabled().then(r => setDevEnabled(r.enabled)).catch(() => { });
  }, []);

  async function resetAttempt(examId: string) {
    if (!confirm('Reset your attempt? This deletes all your answers for this exam.')) return;
    try {
      await api.resetMyAttempt(examId);
      const fresh = await api.listExams();
      setExams(fresh);
      toast.success('Attempt reset.');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to reset.';
      alert(msg);
      toast.error(msg);
    }
  }

  function logout() {
    auth.clear();
    nav('/login');
  }

  async function deleteExam(examId: string, title: string) {
    if (!confirm(`Delete exam “${title}”? This removes its sections and questions. This cannot be undone.`)) return;
    try {
      await api.deleteExam(examId);
      setExams(await api.listExams());
      toast.success('Exam deleted.');
    } catch (e: any) {
      // Surfaces the "has student attempts" conflict, etc.
      const msg = e?.message ?? 'Failed to delete exam.';
      alert(msg);
      toast.error(msg);
    }
  }

  const isStaff = user?.role === 'Faculty' || user?.role === 'Admin' || user?.role === 'Staff';

  // A student has "completed" an exam once their attempt reached a terminal state.
  const isCompleted = (e: ExamSummary) =>
    e.attemptStatus === 'Submitted' || e.attemptStatus === 'AutoSubmitted' || e.attemptStatus === 'Disqualified';

  // The exam window has closed once its scheduled end time has passed — it can no longer be started.
  const windowEnded = (e: ExamSummary) => new Date(e.scheduledEnd).getTime() <= Date.now();

  const examMeta = (e: ExamSummary) => (
    <>
      <div style={{ fontWeight: 600 }}>{e.title}</div>
      <div className="muted">
        {e.batchCode ? `${e.batchCode} · ${e.batchName} · ` : ''}{e.questionCount} questions · {e.totalMarks} marks · {e.durationMinutes} min
      </div>
      <div className="muted" style={{ fontSize: 12 }}>
        Window: {new Date(e.scheduledStart).toLocaleString()} — {new Date(e.scheduledEnd).toLocaleString()}
      </div>
    </>
  );

  const statusLabel = (e: ExamSummary) =>
    e.attemptStatus === 'Disqualified' ? 'Disqualified' : 'Submitted';

  // ----- Student view: Available vs Completed -----
  const publishedForStudent = (exams ?? []).filter(e => e.status === 'Published');
  // Available = published and not yet completed. Closed-window exams still show, but as "Time over".
  const availableExams = publishedForStudent.filter(e => !isCompleted(e));
  const completedExams = publishedForStudent.filter(e => isCompleted(e));

  // Students get one section at a time, chosen by the sidebar (Available vs Attempted).
  const studentTab = mode === 'attempted' ? 'attempted' : 'available';

  const studentContent = (
    <>
      {error && <div className="error">{error}</div>}
      {!exams && !error && <div className="card"><div className="muted">Loading…</div></div>}

      {studentTab === 'available' && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Available exams</h2>
          {exams && availableExams.length === 0 && <div className="muted">No exams available right now.</div>}
          {availableExams.length > 0 && (
            <div className="exam-list">
              {availableExams.map(e => (
                <div key={e.id} className="exam-item">
                  <div>{examMeta(e)}</div>
                  <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                    {devEnabled && e.attemptStatus && (
                      <button onClick={() => resetAttempt(e.id)}
                              title="Dev only — wipe your attempt and answers for this exam">
                        Reset attempt
                      </button>
                    )}
                    {windowEnded(e) ? (
                      <span className="badge" style={{ background: 'var(--danger-soft-bg)', color: 'var(--danger-soft-fg)' }}>
                        Time over
                      </span>
                    ) : (
                      <Link to={`/exam/${e.id}`}>
                        <button className="primary">{e.attemptStatus === 'InProgress' ? 'Resume' : 'Start'}</button>
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {studentTab === 'attempted' && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Attempted exams</h2>
          {exams && completedExams.length === 0 && <div className="muted">You haven't completed any exams yet.</div>}
          {completedExams.length > 0 && (
            <div className="exam-list">
              {completedExams.map(e => (
                <div key={e.id} className="exam-item">
                  <div>{examMeta(e)}</div>
                  <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                    {e.resultsPublished ? (
                      <>
                        {e.attemptStatus === 'Disqualified'
                          ? <span className="badge" style={{ background: 'var(--danger-soft-bg)', color: 'var(--danger-soft-fg)' }}>Disqualified</span>
                          : <span className="badge" style={{ background: 'var(--success-soft-bg)', color: 'var(--success-soft-fg)' }}>
                              Result: {e.scoreAwarded ?? 0} / {e.totalMarks}
                            </span>}
                        <Link to={`/exam/${e.id}/result`}>
                          <button className="primary">View result</button>
                        </Link>
                      </>
                    ) : (
                      <>
                        <span className="badge">{statusLabel(e)}</span>
                        <span className="muted" style={{ fontSize: 12 }}>
                          {e.resultPublishAt
                            ? `Result on ${new Date(e.resultPublishAt).toLocaleString()}`
                            : 'Result awaited'}
                        </span>
                      </>
                    )}
                    {devEnabled && (
                      <button onClick={() => resetAttempt(e.id)}
                              title="Dev only — wipe your attempt and answers for this exam">
                        Reset attempt
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );

  // ----- Faculty / admin view -----
  const staffTitle = mode === 'draft' ? 'Draft exams'
    : mode === 'published' ? 'Published exams'
      : mode === 'toEvaluate' ? 'To evaluate'
        : mode === 'results' ? 'Results'
          : 'Exams';
  const staffEmpty = mode === 'draft' ? 'No draft exams.'
    : mode === 'published' ? 'No published exams.'
      : mode === 'toEvaluate' ? 'Nothing to evaluate — every submission is graded.'
        : mode === 'results' ? 'No exams are ready for results yet.'
          : 'No exams yet.';
  const staffExams = (exams ?? []).filter(e =>
    mode === 'draft' ? e.status === 'Draft'
      : mode === 'published' ? e.status === 'Published'
        // Published exams that have submissions still awaiting grading.
        : mode === 'toEvaluate' ? (e.status === 'Published' && e.submissionCount > 0 && !e.evaluationComplete)
          // Published exams ready to release results (fully graded) or already scheduled.
          : mode === 'results' ? (e.status === 'Published' && (e.evaluationComplete || e.resultPublishAt != null))
            : true);

  // Cascading filter options, derived from the exams in this view (so we never offer an
  // option that yields no results). Each level is narrowed by the levels above it.
  const distinct = (rows: { id: string; name: string }[]) => {
    const seen = new Map<string, string>();
    rows.forEach(r => { if (!seen.has(r.id)) seen.set(r.id, r.name); });
    return [...seen].map(([id, name]) => ({ id, name }));
  };
  const deptOptions = distinct(
    staffExams.filter(e => e.departmentId).map(e => ({ id: e.departmentId!, name: e.departmentName ?? '—' })));
  const programOptions = distinct(
    staffExams.filter(e => e.programId && (!fDept || e.departmentId === fDept))
      .map(e => ({ id: e.programId!, name: e.programName ?? '—' })));
  const batchOptions = distinct(
    staffExams.filter(e => e.batchId && (!fDept || e.departmentId === fDept) && (!fProgram || e.programId === fProgram))
      .map(e => ({ id: e.batchId!, name: e.batchCode ? `${e.batchCode} · ${e.batchName}` : (e.batchName ?? '—') })));

  const visibleExams = staffExams.filter(e =>
    (!fDept || e.departmentId === fDept)
    && (!fProgram || e.programId === fProgram)
    && (!fBatch || e.batchId === fBatch));

  const filtersActive = !!(fDept || fProgram || fBatch);
  const clearFilters = () => { setFDept(''); setFProgram(''); setFBatch(''); };

  const filterBar = (
    <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
      <label style={{ flex: '1 1 180px' }}>
        <div className="muted">Department</div>
        <select value={fDept} onChange={e => { setFDept(e.target.value); setFProgram(''); setFBatch(''); }}>
          <option value="">All departments</option>
          {deptOptions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </label>
      <label style={{ flex: '1 1 180px' }}>
        <div className="muted">Program</div>
        <select value={fProgram} onChange={e => { setFProgram(e.target.value); setFBatch(''); }}>
          <option value="">All programs</option>
          {programOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <label style={{ flex: '1 1 180px' }}>
        <div className="muted">Batch</div>
        <select value={fBatch} onChange={e => setFBatch(e.target.value)}>
          <option value="">All batches</option>
          {batchOptions.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </label>
      {filtersActive && (
        <button type="button" onClick={clearFilters}>Clear</button>
      )}
    </div>
  );

  const staffContent = (
    <>
      {(mode === 'all' || mode === 'draft') && (
        <div className="card row" style={{ justifyContent: 'space-between' }}>
          <div className="muted">Create a new exam from scratch — add sections and questions, then publish.</div>
          <Link to={newExamPath}><button className="primary">+ New exam</button></Link>
        </div>
      )}
      <div className="card">
        <h2 style={{ marginTop: 0 }}>{staffTitle}</h2>
        {exams && staffExams.length > 0 && filterBar}
        {error && <div className="error">{error}</div>}
        {!exams && !error && <div className="muted">Loading…</div>}
        {exams && staffExams.length === 0 && <div className="muted">{staffEmpty}</div>}
        {exams && staffExams.length > 0 && visibleExams.length === 0 && (
          <div className="muted">No exams match the selected filters.</div>
        )}
        {visibleExams.length > 0 && (
          <div className="exam-list">
            {visibleExams.map(e => (
              <div key={e.id} className="exam-item">
                <div>{examMeta(e)}</div>
                <div className="row" style={{ gap: 8 }}>
                  <span className="muted" style={{ fontSize: 12 }}>{e.status}</span>
                  {e.status === 'Published' && (
                    <span className="muted" style={{ fontSize: 12 }}>
                      · {e.submissionCount} submission{e.submissionCount === 1 ? '' : 's'}
                    </span>
                  )}
                  <Link to={authorPath(e.id)}>
                    <button>
                      {(e.status === 'Draft' || (e.status === 'Published' && user?.role === 'Admin'))
                        ? 'Edit' : 'View'}
                    </button>
                  </Link>
                  <RecordInfoButton entity="exams" id={e.id} className="" label="Info" />
                  {e.status === 'Published' && mode !== 'published' && (
                    <Link to={reviewPath(e.id)}>
                      <button className="primary">Review submissions</button>
                    </Link>
                  )}
                  {mode === 'results' && e.status === 'Published' && e.submissionCount > 0 && (
                    <button
                      disabled={!!dl}
                      title="Download all students' answer sheets (answers only) as a ZIP of PDFs"
                      onClick={() => downloadExamSheets(e)}
                    >
                      {dl?.examId === e.id
                        ? `Preparing… ${dl.done}/${dl.total}`
                        : '⬇ Download answer sheets'}
                    </button>
                  )}
                  {mode === 'results' && e.status === 'Published' && (
                    <button
                      disabled={!e.evaluationComplete}
                      title={e.evaluationComplete
                        ? 'Publish results to students'
                        : 'Available once every submission has been graded'}
                      onClick={() => openResultModal(e)}
                    >
                      {e.resultPublishAt ? 'Reschedule result' : 'Publish result'}
                    </button>
                  )}
                  {mode === 'results' && e.status === 'Published' && e.resultPublishAt && (
                    <span className="muted" style={{ fontSize: 12 }}>
                      Results: {new Date(e.resultPublishAt).toLocaleString()}
                    </span>
                  )}
                  {e.status === 'Draft' && (
                    <button className="danger" onClick={() => deleteExam(e.id, e.title)}>Delete</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {resultFor && (
        <div className="rte-modal-backdrop" onMouseDown={() => !savingResult && setResultFor(null)}>
          <div className="rte-modal" onMouseDown={ev => ev.stopPropagation()}>
            <h3>Publish results — {resultFor.title}</h3>
            <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
              Choose when results become visible to students. Pick a future time to schedule the release.
            </p>
            <label>
              <div className="muted" style={{ marginBottom: 4 }}>Result publish date &amp; time</div>
              <input type="datetime-local" value={publishAt} onChange={ev => setPublishAt(ev.target.value)} />
            </label>
            <div className="rte-modal-actions">
              <button type="button" onClick={() => setResultFor(null)} disabled={savingResult}>Cancel</button>
              <button type="button" className="primary" onClick={confirmPublishResult} disabled={savingResult || !publishAt}>
                {savingResult ? 'Publishing…' : 'Publish results'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const content = isStaff ? staffContent : studentContent;

  // Admins author inside the org sidebar; students get their own sidebar (Available / Attempted).
  if (embedded) return <OrgLayout><h1 style={{ marginTop: 0 }}>Exams</h1>{content}</OrgLayout>;
  if (!isStaff) return <StudentLayout>{content}</StudentLayout>;

  // Faculty standalone page.
  return (
    <>
      <header className="topbar">
        <div className="brand">Exam App</div>
        <div className="row" style={{ gap: 12 }}>
          {user?.role === 'Admin' && (
            <Link to="/platform/organizations"><button className="topbar-ghost">← Organizations</button></Link>
          )}
          <span style={{ fontSize: 13 }}>{user?.fullName} · {user?.role}</span>
          <button onClick={logout} className="topbar-ghost">Logout</button>
        </div>
      </header>
      <div className="container">{content}</div>
    </>
  );
}
